import DOMPurify from 'dompurify';
import dayjs from 'dayjs';
import isBoolean from 'lodash/isBoolean';
import isEmpty from 'lodash/isEmpty';
import diff from 'diffObj';

import Icon from '@ant-design/icons';

import {
  setLocaleInLocalStorage,
  getLocaleFromLocalStorage,
  isDashboardArabic
} from 'utils/intl-wrapper';
import { LOCALE } from 'constants/intl-wrapper';
import { COUNTRIES } from 'constants/country-data';
import {
  END_DATES_KEYS,
  COUNTRIES_TIME_ZONE,
  isSaudi
} from 'constants/helpers';
import { SLLR_REG_SRC, SLLR_SOURCE } from 'constants/signup';
import { getCurrentUserCountryData } from 'constants/countries/countries-mapping';
import { fmt } from 'components/IntlWrapper/IntlWrapper';

import { ReactComponent as SllrLogoEnglishLarge } from 'assets/icons/sllr-large-english-icon.svg';
import { ReactComponent as SllrLogoArabicLarge } from 'assets/icons/sllr-large-arabic-icon.svg';
import { ReactComponent as SllrLogoArabicMedium } from 'assets/icons/sllr-medium-arabic-icon.svg';
import { ReactComponent as SllrLogoEnglishMedium } from 'assets/icons/sllr-medium-english-icon.svg';
import { ReactComponent as SllrLogoArabicSmall } from 'assets/icons/sllr-small-arabic-icon.svg';
import { ReactComponent as SllrLogoEnglishSmall } from 'assets/icons/sllr-small-english-icon.svg';
import { ReactComponent as SllrCutLogoLarge } from 'assets/icons/sllr-large-cut-icon.svg';
import { ReactComponent as SllrCutLogoMedium } from 'assets/icons/sllr-medium-cut-icon.svg';
import { ReactComponent as SllrCutLogoSmall } from 'assets/icons/sllr-small-cut-icon.svg';
import { ReactComponent as SllrCutLogoXsmall } from 'assets/icons/sllr-green-icon-xsmall.svg';
import SllrLogoEnglishLargeIcon from 'assets/icons/sllr-large-english-icon.svg';
import SllrLogoArabicLargeIcon from 'assets/icons/sllr-large-arabic-icon.svg';
import SllrLogoArabicMediumIcon from 'assets/icons/sllr-medium-arabic-icon.svg';
import SllrLogoEnglishMediumIcon from 'assets/icons/sllr-medium-english-icon.svg';
import SllrLogoArabicSmallIcon from 'assets/icons/sllr-small-arabic-icon.svg';
import SllrLogoEnglishSmallIcon from 'assets/icons/sllr-small-english-icon.svg';
import SllrCutLogoLargeIcon from 'assets/icons/sllr-large-cut-icon.svg';
import SllrCutLogoMediumIcon from 'assets/icons/sllr-medium-cut-icon.svg';
import SllrCutLogoSmallIcon from 'assets/icons/sllr-small-cut-icon.svg';
import SllrCutLogoXsmallIcon from 'assets/icons/sllr-green-icon-xsmall.svg';

import { ReactComponent as SllrPayLargeEnglish } from 'assets/icons/sllr-pay-large-english.svg';
import { ReactComponent as SllrPayLargeArabic } from 'assets/icons/sllr-pay-large-arabic.svg';
import { ReactComponent as SllrPayMediumArabic } from 'assets/icons/sllr-pay-medium-arabic.svg';
import { ReactComponent as SllrPayMediumEnglish } from 'assets/icons/sllr-pay-medium-english.svg';
import { ReactComponent as SllrPaySmallEnglish } from 'assets/icons/sllr-pay-small-english.svg';
import { ReactComponent as SllrPaySmallArabic } from 'assets/icons/sllr-pay-small-arabic.svg';

import { ReactComponent as SllrShipLargeArabic } from 'assets/icons/sllr-ship-large-arabic.svg';
import { ReactComponent as SllrShipLargeEnglish } from 'assets/icons/sllr-ship-large-english.svg';
import { ReactComponent as SllrShipMediumEnglish } from 'assets/icons/sllr-ship-medium-english.svg';
import { ReactComponent as SllrShipMediumArabic } from 'assets/icons/sllr-ship-medium-arabic.svg';
import { ReactComponent as SllrShipSmallArabic } from 'assets/icons/sllr-ship-small-arabic.svg';
import { ReactComponent as SllrShipSmallEnglish } from 'assets/icons/sllr-ship-small-english.svg';
import { ReactComponent as SllrShipSideBar } from 'assets/icons/sllr-ship-side-bar-icon.svg';
import { ReactComponent as SllrShipSideBarArabic } from 'assets/icons/sllr-ship-side-bar-arabic-icon.svg';

import { ReactComponent as SllrPosLargeEnglish } from 'assets/icons/sllr-pos-large-english.svg';
import { ReactComponent as SllrPosLargeArabic } from 'assets/icons/sllr-pos-large-arabic.svg';
import { ReactComponent as SllrPosMediumArabic } from 'assets/icons/sllr-pos-medium-arabic.svg';
import { ReactComponent as SllrPosMediumEnglish } from 'assets/icons/sllr-pos-medium-english.svg';
import { ReactComponent as SllrPosSmallEnglish } from 'assets/icons/sllr-pos-small-english.svg';
import { ReactComponent as SllrPosSmallArabic } from 'assets/icons/sllr-pos-small-arabic.svg';

import { ReactComponent as SllrStorageLargeArabic } from 'assets/icons/sllr-storage-large-arabic.svg';
import { ReactComponent as SllrStorageLargeEnglish } from 'assets/icons/sllr-storage-large-english.svg';
import { ReactComponent as SllrStorageMediumEnglish } from 'assets/icons/sllr-storage-medium-english.svg';
import { ReactComponent as SllrStorageMediumArabic } from 'assets/icons/sllr-storage-medium-arabic.svg';
import { ReactComponent as SllrStorageSmallArabic } from 'assets/icons/sllr-storage-small-arabic.svg';
import { ReactComponent as SllrStorageSmallEnglish } from 'assets/icons/sllr-storage-small-english.svg';

import SllrPayLargeEnglishIcon from 'assets/icons/sllr-pay-large-english.svg';
import SllrPayLargeArabicIcon from 'assets/icons/sllr-pay-large-arabic.svg';
import SllrPayMediumArabicIcon from 'assets/icons/sllr-pay-medium-arabic.svg';
import SllrPayMediumEnglishIcon from 'assets/icons/sllr-pay-medium-english.svg';
import SllrPaySmallEnglishIcon from 'assets/icons/sllr-pay-small-english.svg';
import SllrPaySmallArabicIcon from 'assets/icons/sllr-pay-small-arabic.svg';

import SllrShipLargeArabicIcon from 'assets/icons/sllr-ship-large-arabic.svg';
import SllrShipLargeEnglishIcon from 'assets/icons/sllr-ship-large-english.svg';
import SllrShipMediumEnglishIcon from 'assets/icons/sllr-ship-medium-english.svg';
import SllrShipMediumArabicIcon from 'assets/icons/sllr-ship-medium-arabic.svg';
import SllrShipSmallArabicIcon from 'assets/icons/sllr-ship-small-arabic.svg';
import SllrShipSmallEnglishIcon from 'assets/icons/sllr-ship-small-english.svg';
import SllrShipSideBarIcon from 'assets/icons/sllr-ship-side-bar-icon.svg';
import SllrShipSideBarArabicIcon from 'assets/icons/sllr-ship-side-bar-arabic-icon.svg';

import SllrPosLargeEnglishIcon from 'assets/icons/sllr-pos-large-english.svg';
import SllrPosLargeArabicIcon from 'assets/icons/sllr-pos-large-arabic.svg';
import SllrPosMediumArabicIcon from 'assets/icons/sllr-pos-medium-arabic.svg';
import SllrPosMediumEnglishIcon from 'assets/icons/sllr-pos-medium-english.svg';
import SllrPosSmallEnglishIcon from 'assets/icons/sllr-pos-small-english.svg';
import SllrPosSmallArabicIcon from 'assets/icons/sllr-pos-small-arabic.svg';

import SllrStorageLargeArabicIcon from 'assets/icons/sllr-storage-large-arabic.svg';
import SllrStorageLargeEnglishIcon from 'assets/icons/sllr-storage-large-english.svg';
import SllrStorageMediumEnglishIcon from 'assets/icons/sllr-storage-medium-english.svg';
import SllrStorageMediumArabicIcon from 'assets/icons/sllr-storage-medium-arabic.svg';
import SllrStorageSmallArabicIcon from 'assets/icons/sllr-storage-small-arabic.svg';
import SllrStorageSmallEnglishIcon from 'assets/icons/sllr-storage-small-english.svg';

const { sanitize } = DOMPurify;
const javascriptPurify = (dirty) => dirty.replace(/^(javascript\:)/, '');
const { codeNumber, removeZeroWithCodeNumber } = getCurrentUserCountryData();

export const cleanEmptyString = (obj) => {
  if (dayjs.isDayjs(obj)) return obj;
  if (Array.isArray(obj)) {
    return obj
      .map((v) => (v && typeof v === 'object' ? cleanEmptyString(v) : v))
      .filter((v) => !(v === '' || v === undefined || v === null));
  } else {
    return Object.entries(obj)
      .map(([k, v]) => [
        k,
        v && typeof v === 'object' ? cleanEmptyString(v) : v
      ])
      .reduce(
        (a, [k, v]) =>
          v === '' || v === null || v === undefined ? a : ((a[k] = v), a),
        {}
      );
  }
};

export const handleLanguageChange = async (value, redirectTo = false) => {
  await setLocaleInLocalStorage(value);
  redirectTo && window.history.replaceState(null, null, redirectTo);
  window.location.reload();
};

export const secondsToTime = (secs) => {
  const divisor_for_minutes = secs % (60 * 60);
  const minutes = Math.floor(divisor_for_minutes / 60);

  const divisor_for_seconds = divisor_for_minutes % 60;
  const seconds = Math.ceil(divisor_for_seconds);

  if (getLocaleFromLocalStorage() === LOCALE.EN)
    return ` ${minutes < 10 ? `0${minutes}` : minutes} : ${
      seconds < 10 ? `0${seconds}` : seconds
    }`;
  else {
    return ` ${seconds < 10 ? `0${seconds}` : seconds} : ${
      minutes < 10 ? `0${minutes}` : minutes
    }`;
  }
};

export const numberRegex = /^\d+$/;

export const alphanumericRegex = /^[a-zA-Z0-9]+$/;

export const countDown = (values) => {
  const { timer, setTimer, setDisabled } = values;
  return setInterval(() => {
    if (timer > 0) {
      setTimer(timer - 1);
    }
    if (timer === 0) {
      setDisabled(false);
    }
  }, 1000);
};

export const getMinIntoSec = (minutes) => {
  return minutes * 60;
};

export const sleep = (ms) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

/**
 * sanitize text from xss attacks to remove malicious injection of javascript code
 * @param text
 */

export const sanitizeText = (text) =>
  text && !isBoolean(text) ? javascriptPurify(sanitize(text)) : text;

/**
 * sanitize key-value strings maps
 * @param obj
 * @returns {{}}
 */
export const sanitizePlainObject = (obj) =>
  Object.entries(obj)
    .map(([key, value]) => [key, sanitizeText(value)])
    .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

/**
 * sanitize obj with nested objects and other types
 * @param obj
 * @returns {{}|*}
 */
export const sanitizeAll = (obj) => {
  // test for moment objects, undefined, numbers, or booleans
  if (
    dayjs.isDayjs(obj) ||
    typeof obj === 'undefined' ||
    typeof obj === 'number' ||
    typeof obj === 'boolean'
  ) {
    if (dayjs.isDayjs(obj)) return formatDateUTC(obj);
    else return obj;
  }
  // test for strings
  if (typeof obj !== 'object') {
    return sanitizeText(obj);
  }
  // sanitize object and its nested items after being cleansed from empty and undefined values
  return Object.entries(cleanEmptyString(obj))
    .map(([key, value]) => [
      key,
      typeof value === 'object' && !dayjs.isDayjs(value)
        ? Array.isArray(value)
          ? value.map((item) => sanitizeAll(item))
          : sanitizeAll(value)
        : typeof value === 'number'
        ? value
        : dayjs.isDayjs(value)
        ? formatDateUTC(value, key)
        : sanitizeText(value)
    ])
    .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
};

export const dates = (value, format, timeZone) => {
  const date = format
    ? dayjs(value)
        .utcOffset(timeZone?.utcOffset || COUNTRIES_TIME_ZONE.egypt)
        .format(format)
    : dayjs(value).utcOffset(timeZone?.utcOffset || COUNTRIES_TIME_ZONE.egypt);
  if (isDashboardArabic()) {
    return date?.replace(/\d/g, (d) => '٠١٢٣٤٥٦٧٨٩'[d]);
  } else return date;
};

const formatDateUTC = (date, key) => {
  if (END_DATES_KEYS.includes(key)) {
    return date
      .utc()
      .set('hour', 23)
      .set('minute', 59)
      .set('second', 59)
      .format();
  }
  return date.utc().set('hour', 0).set('minute', 0).set('second', 0).format();
};

export const regexNumber = /^\d+$/;
export const regexNumberWithNegative = /^-?\d*$/;

export const regexPhoneNumber = isSaudi()
  ? /^\+?(?:[\d -]*)+$/
  : /^\+?(?:[\d]*)$/;

export const toString = (obj) => {
  Object.keys(obj).forEach((key) => {
    if (typeof obj[key] === 'object') {
      return toString(obj[key]);
    }

    obj[key] = '' + obj[key];
  });

  return obj;
};

export const arabicCurrency = (country) => {
  return country?.codeName === COUNTRIES[1]?.codeName
    ? 'ريال سعودى'
    : 'جنيه مصرى';
};

export const convertEnglishNumbersToArabic = (number) => {
  const dashboardLanguage = getLocaleFromLocalStorage();
  return dashboardLanguage === LOCALE.AR
    ? number?.toString()?.replace(/\d/g, (d) => '٠١٢٣٤٥٦٧٨٩'[d])
    : number;
};

export const checkDate = (date) => {
  if (dayjs(date).isToday()) {
    return fmt({ id: `date_picker.filter_option.today` });
  } else if (dayjs(date).isYesterday()) {
    return fmt({ id: `date_picker.filter_option.yesterday` });
  } else if (dayjs(date).isTomorrow()) {
    return fmt({ id: `date_picker.filter_option.tomorrow` });
  } else {
    return;
  }
};

export const checkSmartDate = (
  date,
  format = 'ddd, DD MMM',
  extraFormat = false
) => {
  const formattedDate = checkDate(date);
  if (formattedDate) {
    if (extraFormat) {
      return `${formattedDate} ${dates(date, 'DD MMM')}`;
    }
    return formattedDate;
  } else {
    return dates(date, format);
  }
};

export const isDatePassed = (date, interval) => {
  return dayjs(date).isBefore(dayjs(), interval);
};

export const generateUUID = () => {
  // Public Domain/MIT
  let d = new Date().getTime(); //Timestamp
  let d2 =
    (typeof performance !== 'undefined' &&
      performance.now &&
      performance.now() * 1000) ||
    0; //Time in microseconds since page-load or 0 if unsupported
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    let r = Math.random() * 16; //random number between 0 and 16
    if (d > 0) {
      //Use timestamp until depleted
      r = (d + r) % 16 | 0;
      d = Math.floor(d / 16);
    } else {
      //Use microseconds since page-load if supported
      r = (d2 + r) % 16 | 0;
      d2 = Math.floor(d2 / 16);
    }
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
};

export const shortenString = (str = '', maxLength) => {
  if (str && str?.length > maxLength) {
    return str.slice(0, maxLength) + '...';
  } else {
    return str;
  }
};

export const userInfo = JSON.parse(localStorage.getItem('userInfo'))?.user;

export const isUserAuth = () => {
  let userInfo = localStorage.getItem('userInfo');
  if (userInfo) {
    userInfo = JSON.parse(localStorage.getItem('userInfo'));
    const isAuth =
      userInfo?.user?.isPhoneVerified &&
      userInfo?.user?.businessAdminInfo?.businessId;
    return isAuth ? userInfo?.token : false;
  }
  return false;
};

export const isTwoArraysOfStringEqual = (arr1, arr2) => {
  // For sorted and unsorted
  // Ex.1: ['a','b','c'] === ['a','b','c'] returns true
  // Ex.2: ['a','b','c'] === ['b','c','a'] returns true

  if (arr1?.length !== arr2?.length) return false;

  const isInArray1 = arr1.every((item) => arr2.find((item2) => item === item2));
  const isInArray2 = arr2.every((item) => arr1.find((item2) => item === item2));

  return isInArray1 && isInArray2;
};

export const stringToBoolean = (stringValue) => {
  if (!isNaN(stringValue)) {
    return !!stringValue;
  }
  switch (stringValue?.toLowerCase()?.trim()) {
    case 'true':
    case 'yes':
    case '1':
      return true;

    case 'false':
    case 'no':
    case '0':
    case null:
    case undefined:
      return false;

    default:
      return JSON.parse(stringValue);
  }
};

export const sortArrayByDate = (arr) => {
  const sortedArr = arr.sort(
    (a, b) =>
      new Date(a.scheduledDate).getTime() - new Date(b.scheduledDate).getTime()
  );
  return sortedArr;
};

export const getEditChanges = ({ beforeChangeObj, afterChangeObj }) => {
  const diffs = diff(beforeChangeObj, afterChangeObj);
  const unset = {};

  Object.keys(diffs).forEach((key) => {
    const isDeletedValue =
      [undefined, null, ''].includes(diffs[key]) ||
      (typeof diffs[key] === 'object' && isEmpty(diffs[key]));

    if (isDeletedValue) {
      if (afterChangeObj[key] !== beforeChangeObj[key]) {
        unset[key] = 1;
      }
      delete diffs[key];
    }
  });

  const changesPayload = {
    ...diffs,
    ...(!isEmpty(unset) && {
      unset
    })
  };

  return changesPayload;
};

export const hasDigits = (string) => {
  return /\d/.test(string);
};

export const getMobilePhoneNumber = (modifiedPhone) => {
  let phoneWithoutCountryCode = '';
  let phoneWithCountryCode = '';

  if (modifiedPhone) {
    if (modifiedPhone[0] === '+') {
      const regex = new RegExp(`^${codeNumber.replace('+', '\\+')}`);
      if (regex.test(modifiedPhone)) {
        phoneWithCountryCode = modifiedPhone;
        if (removeZeroWithCodeNumber) {
          phoneWithoutCountryCode = `0${modifiedPhone?.substring(
            codeNumber.length
          )}`;
        }
      }
    } else {
      if (removeZeroWithCodeNumber && modifiedPhone[0] === '0') {
        phoneWithCountryCode = `${codeNumber}${modifiedPhone?.substring(1)}`;
      } else {
        phoneWithCountryCode = `${codeNumber}${modifiedPhone}`;
      }
      phoneWithoutCountryCode = modifiedPhone;
    }
  }
  return {
    phoneWithCountryCode,
    phoneWithoutCountryCode
  };
};

export const convertArrayToNestedObject = (array, value) => {
  //  This function to deal with handlechangeform function
  // if it takes array, It converts the array to nested object so it can set form fields
  const result = {};
  let nestedObj = result;
  array.forEach((key, index) => {
    nestedObj[key] = {};
    if (index === array.length - 1) {
      nestedObj[key] = value;
    } else {
      nestedObj = nestedObj[key];
    }
  });
  return result;
};

export const isSllrUser = () => {
  const isSllrRegSrc =
    JSON.parse(localStorage.getItem('userInfo'))?.user?.regSrc === SLLR_REG_SRC;

  if (isUserAuth()) {
    return isSllrRegSrc;
  }

  return true;
};

export const isFromSllrDashboard = () => {
  const { search } = window.location;

  return new URLSearchParams(search).get('utm_source') === SLLR_SOURCE;
};

export const getAccountTypePropForSuccessMetrics = () => ({
  ...(isSllrUser() && {
    Account_Type: 'Sllr'
  })
});

export const normalizeText = (text) => {
  if (!text) {
    return;
  }

  //normalize Arabic
  text = text.replace(/(آ|إ|أ)/g, 'ا');
  text = text.replace(/(ة)/g, 'ه');
  text = text.replace(/(ئ|ؤ)/g, 'ء');
  text = text.replace(/(ى)/g, 'ي');

  return text;
};

export const isArabic = (text) => /[\u0600-\u06FF]/.test(text);

export const getCityName = ({
  cityName,
  cityOtherName,
  typingLanguage,
  locale
}) => (typingLanguage !== locale ? cityOtherName : cityName);

export const getAreaName = ({ area, typingLanguage, locale, availability }) => {
  const { zoneName, districtName, zoneOtherName, districtOtherName } = area;
  if (
    (area?.[availability] && districtName === 'Others') ||
    districtName !== 'Others'
  ) {
    const adjustedZoneName =
      typingLanguage !== locale ? zoneOtherName : zoneName;
    const adjustedDistrictName =
      typingLanguage !== locale ? districtOtherName : districtName;

    return zoneName === districtName
      ? adjustedZoneName
      : `${adjustedZoneName} - ${adjustedDistrictName}`;
  }
};

export const SLLR_ICON = ({
  asComponent = true,
  size = 'large',
  isCut = false,
  wrapComponent = true
}) => {
  const iconMapper = {
    large: {
      component: {
        ar: wrapComponent ? <SllrLogoArabicLarge /> : SllrLogoArabicLarge,
        en: wrapComponent ? <SllrLogoEnglishLarge /> : SllrLogoEnglishLarge,
        cut: wrapComponent ? <SllrCutLogoLarge /> : SllrCutLogoLarge
      },
      element: {
        ar: SllrLogoArabicLargeIcon,
        en: SllrLogoEnglishLargeIcon,
        cut: SllrCutLogoLargeIcon
      }
    },
    medium: {
      component: {
        ar: wrapComponent ? <SllrLogoArabicMedium /> : SllrLogoArabicMedium,
        en: wrapComponent ? <SllrLogoEnglishMedium /> : SllrLogoEnglishMedium,
        cut: wrapComponent ? <SllrCutLogoMedium /> : SllrCutLogoMedium
      },
      element: {
        ar: SllrLogoArabicMediumIcon,
        en: SllrLogoEnglishMediumIcon,
        cut: SllrCutLogoMediumIcon
      }
    },
    small: {
      component: {
        ar: wrapComponent ? <SllrLogoArabicSmall /> : SllrLogoArabicSmall,
        en: wrapComponent ? <SllrLogoEnglishSmall /> : SllrLogoEnglishSmall,
        cut: wrapComponent ? <SllrCutLogoSmall /> : SllrCutLogoSmall
      },
      element: {
        ar: SllrLogoArabicSmallIcon,
        en: SllrLogoEnglishSmallIcon,
        cut: SllrCutLogoSmallIcon
      }
    },
    xsmall: {
      component: {
        cut: wrapComponent ? <SllrCutLogoXsmall /> : SllrCutLogoXsmall
      },
      element: {
        cut: SllrCutLogoXsmallIcon
      }
    }
  };
  let icon = iconMapper[size] || iconMapper.large;
  icon = asComponent ? icon.component : icon.element;

  return isCut ? icon.cut : isDashboardArabic() ? icon.ar : icon.en;
};

export const SLLR_PAY_ICON = ({
  asComponent = true,
  size = 'large',
  wrapComponent = true
}) => {
  const iconMapper = {
    large: {
      component: {
        ar: wrapComponent ? <SllrPayLargeArabic /> : SllrPayLargeArabic,
        en: wrapComponent ? <SllrPayLargeEnglish /> : SllrPayLargeEnglish
      },
      element: {
        ar: SllrPayLargeArabicIcon,
        en: SllrPayLargeEnglishIcon
      }
    },
    medium: {
      component: {
        ar: wrapComponent ? <SllrPayMediumArabic /> : SllrPayMediumArabic,
        en: wrapComponent ? <SllrPayMediumEnglish /> : SllrPayMediumEnglish
      },
      element: {
        ar: SllrPayMediumArabicIcon,
        en: SllrPayMediumEnglishIcon
      }
    },
    small: {
      component: {
        ar: wrapComponent ? <SllrPaySmallArabic /> : SllrPaySmallArabic,
        en: wrapComponent ? <SllrPaySmallEnglish /> : SllrPaySmallEnglish
      },
      element: {
        ar: SllrPaySmallArabicIcon,
        en: SllrPaySmallEnglishIcon
      }
    }
  };
  let icon = iconMapper[size] || iconMapper.large;
  icon = asComponent ? icon.component : icon.element;

  return isDashboardArabic() ? icon.ar : icon.en;
};

export const SLLR_SHIP_ICON = ({
  asComponent = true,
  size = 'large',
  wrapComponent = true
}) => {
  const iconMapper = {
    large: {
      component: {
        ar: wrapComponent ? <SllrShipLargeArabic /> : SllrShipLargeArabic,
        en: wrapComponent ? <SllrShipLargeEnglish /> : SllrShipLargeEnglish
      },
      element: {
        ar: SllrShipLargeArabicIcon,
        en: SllrShipLargeEnglishIcon
      }
    },
    medium: {
      component: {
        ar: wrapComponent ? <SllrShipMediumArabic /> : SllrShipMediumArabic,
        en: wrapComponent ? <SllrShipMediumEnglish /> : SllrShipMediumEnglish
      },
      element: {
        ar: SllrShipMediumArabicIcon,
        en: SllrShipMediumEnglishIcon
      }
    },
    small: {
      component: {
        ar: wrapComponent ? <SllrShipSmallArabic /> : SllrShipSmallArabic,
        en: wrapComponent ? <SllrShipSmallEnglish /> : SllrShipSmallEnglish
      },
      element: {
        ar: SllrShipSmallArabicIcon,
        en: SllrShipSmallEnglishIcon
      }
    },
    flexColumn: {
      component: {
        ar: wrapComponent ? <SllrShipSideBarArabic /> : SllrShipSideBarArabic,
        en: wrapComponent ? <SllrShipSideBar /> : SllrShipSideBar
      },
      element: {
        ar: SllrShipSideBarArabicIcon,
        en: SllrShipSideBarIcon
      }
    }
  };
  let icon = iconMapper[size] || iconMapper.large;
  icon = asComponent ? icon.component : icon.element;

  return isDashboardArabic() ? icon.ar : icon.en;
};

export const SLLR_POS_ICON = ({
  asComponent = true,
  size = 'large',
  wrapComponent = true
}) => {
  const iconMapper = {
    large: {
      component: {
        ar: wrapComponent ? <SllrPosLargeArabic /> : SllrPosLargeArabic,
        en: wrapComponent ? <SllrPosLargeEnglish /> : SllrPosLargeEnglish
      },
      element: {
        ar: SllrPosLargeArabicIcon,
        en: SllrPosLargeEnglishIcon
      }
    },
    medium: {
      component: {
        ar: wrapComponent ? <SllrPosMediumArabic /> : SllrPosMediumArabic,
        en: wrapComponent ? <SllrPosMediumEnglish /> : SllrPosMediumEnglish
      },
      element: {
        ar: SllrPosMediumArabicIcon,
        en: SllrPosMediumEnglishIcon
      }
    },
    small: {
      component: {
        ar: wrapComponent ? <SllrPosSmallArabic /> : SllrPosSmallArabic,
        en: wrapComponent ? <SllrPosSmallEnglish /> : SllrPosSmallEnglish
      },
      element: {
        ar: SllrPosSmallArabicIcon,
        en: SllrPosSmallEnglishIcon
      }
    }
  };
  let icon = iconMapper[size] || iconMapper.large;
  icon = asComponent ? icon.component : icon.element;

  return isDashboardArabic() ? icon.ar : icon.en;
};

export const SLLR_STORAGE_ICON = ({
  asComponent = true,
  size = 'large',
  wrapComponent = true
}) => {
  const iconMapper = {
    large: {
      component: {
        ar: wrapComponent ? <SllrStorageLargeArabic /> : SllrStorageLargeArabic,
        en: wrapComponent ? (
          <SllrStorageLargeEnglish />
        ) : (
          SllrStorageLargeEnglish
        )
      },
      element: {
        ar: SllrStorageLargeArabicIcon,
        en: SllrStorageLargeEnglishIcon
      }
    },
    medium: {
      component: {
        ar: wrapComponent ? (
          <SllrStorageMediumArabic />
        ) : (
          SllrStorageMediumArabic
        ),
        en: wrapComponent ? (
          <SllrStorageMediumEnglish />
        ) : (
          SllrStorageMediumEnglish
        )
      },
      element: {
        ar: SllrStorageMediumArabicIcon,
        en: SllrStorageMediumEnglishIcon
      }
    },
    small: {
      component: {
        ar: wrapComponent ? <SllrStorageSmallArabic /> : SllrStorageSmallArabic,
        en: wrapComponent ? (
          <SllrStorageSmallEnglish />
        ) : (
          SllrStorageSmallEnglish
        )
      },
      element: {
        ar: SllrStorageSmallArabicIcon,
        en: SllrStorageSmallEnglishIcon
      }
    }
  };
  let icon = iconMapper[size] || iconMapper.large;
  icon = asComponent ? icon.component : icon.element;

  return isDashboardArabic() ? icon.ar : icon.en;
};

export const stripHTMLTags = (value) => {
  if (!value) {
    return '';
  }
  return value.replace(/(<([^>]+)>)/gi, '');
};

export const isRichTextEmpty = (value) => {
  return value === '<p><br></p>' || stripHTMLTags(value) === '';
};

export const renderImage = (image, renderedIcon) =>
  image ? (
    <img alt="store-profile" src={image} />
  ) : (
    <Icon className="store-icon" component={renderedIcon} />
  );

export const checkEmailProvider = (email) => {
  const regex = /@(outlook|live|hotmail)\./i;
  return regex.test(email);
};

export const searchQueriesToObject = (searchQueries) => {
  const result = {};
  for (const [key, value] of searchQueries.entries()) {
    result[key] = value;
  }
  return result;
};

export const ENGLISH_CHARS = /[a-zA-Z0-9]/;

export const initialName = (businessPage, propertyName, options) => {
  const initials = businessPage[propertyName]
    .split(' ')
    .map((word) => word.charAt(0));

  if (options) {
    return initials[0].toUpperCase();
  } else {
    return initials.join('').toUpperCase();
  }
};

export const isEmptyObject = (obj) => {
  return Object.keys(obj).length === 0;
};

export const isValidString = (str) => {
  return typeof str === 'string' && str.trim().length > 0;
};
